/**
 * Presentation-only formatting helpers for the Knowledge pages. Pure functions
 * mapping domain enums onto UI affordances (badge tones, labels). No data
 * access — safe to import from page components.
 */

import type { BadgeTone } from '../../ui';
import type { ComplexityLevel, SpecDocKind } from '../../features/knowledge/types';

/** Maps a complexity band onto a Badge tone. */
export function complexityTone(level: ComplexityLevel): BadgeTone {
  switch (level) {
    case 'low':
      return 'success';
    case 'medium':
      return 'info';
    case 'high':
      return 'warning';
    case 'very-high':
      return 'danger';
  }
}

/** Human label for a complexity band. */
export function complexityLabel(level: ComplexityLevel): string {
  switch (level) {
    case 'low':
      return 'Low';
    case 'medium':
      return 'Medium';
    case 'high':
      return 'High';
    case 'very-high':
      return 'Very high';
  }
}

/** Human label for a spec document kind. */
export function docKindLabel(kind: SpecDocKind): string {
  switch (kind) {
    case 'overview':
      return 'Overview';
    case 'requirements':
      return 'Requirements';
    case 'architecture':
      return 'Architecture';
    case 'api':
      return 'API';
    case 'data-model':
      return 'Data model';
    case 'analysis':
      return 'Analysis';
    case 'other':
      return 'Doc';
  }
}

/** Badge tone for a spec document kind. */
export function docKindTone(kind: SpecDocKind): BadgeTone {
  switch (kind) {
    case 'architecture':
      return 'accent';
    case 'analysis':
      return 'warning';
    case 'api':
      return 'info';
    case 'data-model':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Tone → Tailwind text color for the complexity meter fill. */
export function complexityBarClass(level: ComplexityLevel): string {
  switch (level) {
    case 'low':
      return 'bg-emerald-500';
    case 'medium':
      return 'bg-sky-500';
    case 'high':
      return 'bg-amber-500';
    case 'very-high':
      return 'bg-red-500';
  }
}
